import { Trees, Download, AlertTriangle, TrendingDown, Layers, BarChart3 } from 'lucide-react';
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { useTerritoryStore } from '@/stores/territoryStore';
import { isDataUnavailable } from '@/services/dataAvailability';
import { ProductionDataUnavailable } from '@/components/common/ProductionDataUnavailable';
import { useZan } from '@/hooks/useZan';
import { IndicatorCard } from '@/components/dashboard/IndicatorCard';

// ── Design tokens (inlined — no theme.ts import) ────────────────────────────

const NAVY = '#1b2559';
const MARIANNE = '#e1000f';
const SUCCESS = '#1f8a5b';
const WARN = '#ef7d18';
const DANGER_BG = '#fdecec';

// ── ZAN constants (must stay in sync with zan_monitor.py) ──────────────────

const ZAN_TARGET_YEAR = 2031;
const CURRENT_YEAR = 2026;

// ── Chart data type ─────────────────────────────────────────────────────────

interface ChartPoint {
  year: number;
  realized: number | null;
  trajectory: number | null;
  trend: number | null;
}

// ── Gauge (SVG semicircle) ──────────────────────────────────────────────────

function Gauge({ pct, color }: { pct: number; color: string }) {
  const R = 34;
  const cx = 50;
  const cy = 44;
  const bgPath = `M ${cx - R} ${cy} A ${R} ${R} 0 0 1 ${cx + R} ${cy}`;

  if (pct <= 0) {
    return (
      <svg width={100} height={60} viewBox="0 0 100 60" aria-label="0% consommé">
        <path d={bgPath} fill="none" stroke="#e4e8f0" strokeWidth={10} strokeLinecap="round" />
        <text x={cx} y={58} textAnchor="middle" fontSize={14} fontWeight={800} fill="#11193a">
          0%
        </text>
      </svg>
    );
  }

  // Angle in standard math (y-up): starts at π (left), ends at 0 (right)
  const clampedPct = Math.min(pct, 100);
  const endAngle = Math.PI - (clampedPct / 100) * Math.PI;
  const endX = (cx + R * Math.cos(endAngle)).toFixed(2);
  const endY = (cy - R * Math.sin(endAngle)).toFixed(2);
  const fillPath = `M ${cx - R} ${cy} A ${R} ${R} 0 0 1 ${endX} ${endY}`;

  return (
    <svg width={100} height={60} viewBox="0 0 100 60" aria-label={`${clampedPct}% consommé`}>
      <path d={bgPath} fill="none" stroke="#e4e8f0" strokeWidth={10} strokeLinecap="round" />
      <path d={fillPath} fill="none" stroke={color} strokeWidth={10} strokeLinecap="round" />
      <text x={cx} y={58} textAnchor="middle" fontSize={14} fontWeight={800} fill="#11193a">
        {clampedPct}%
      </text>
    </svg>
  );
}

// ── SuiviZan ─────────────────────────────────────────────────────────────────

/**
 * Suivi ZAN — 6ème écran Terravision.
 * Aide à la décision : trajectoire de consommation foncière, enveloppe ZAN,
 * rythme requis et décomposition (bâti / voirie).
 * Cadre : Loi Climat & Résilience, Art. 191-199 — objectif ZAN 2031 / 2050.
 */
export function SuiviZan() {
  const { activeTerritory } = useTerritoryStore();
  const insee = activeTerritory?.code_insee ?? null;
  const { data, isLoading, error, refetch } = useZan(insee);

  // ── No territory selected ────────────────────────────────────────────────
  if (!activeTerritory) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <p className="font-sans text-sm text-muted">
          Sélectionnez un territoire pour accéder au Suivi ZAN.
        </p>
      </div>
    );
  }


  if (isDataUnavailable(error)) {
    return (
      <ProductionDataUnavailable
        feature="Suivi ZAN"
        requiredSources="Portail de l'artificialisation et OCS GE historisés"
      />
    );
  }
  // ── Error ─────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div
          className="rounded-panel flex flex-col gap-2 px-5 py-4 text-sm font-medium"
          style={{ background: DANGER_BG, color: MARIANNE }}
          role="alert"
        >
          <span>Erreur lors du chargement des données ZAN.</span>
          <button
            type="button"
            className="self-start font-semibold underline"
            onClick={() => refetch()}
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // ── Loading ───────────────────────────────────────────────────────────────
  if (isLoading || !data) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <p className="font-sans text-sm text-muted animate-pulse">
          Chargement des données ZAN…
        </p>
      </div>
    );
  }

  // ── Derived values ────────────────────────────────────────────────────────
  const reste = Math.max(0, data.envelope_ha - data.consumed_ha);
  const pctConsumed = data.envelope_ha > 0
    ? Math.min(100, Math.round((data.consumed_ha / data.envelope_ha) * 100))
    : 0;
  const currentRate = data.detail.zan.rythme_estime_ha_an;
  const rythmeVariation = currentRate > 0
    ? Math.round(((data.trajectory_required - currentRate) / currentRate) * 100)
    : 0;
  const isAlerte = data.detail.zan.alerte;

  // Decomposition from backend detail
  const batimentsHa = data.detail.detail.batiments_ha;
  const voirieHa = data.detail.detail.voirie_ha;
  const activiteHa = Math.max(0, data.consumed_ha - batimentsHa - voirieHa);

  // ── Chart data (historical + future projection) ────────────────────────────
  const chartData: ChartPoint[] = data.by_year.map(({ year, ha }) => ({
    year,
    realized: ha,
    // Start trajectory and trend at current year
    trajectory: year === CURRENT_YEAR ? data.consumed_ha : null,
    trend: year === CURRENT_YEAR ? data.consumed_ha : null,
  }));

  // Add future years: trajectory (required) + trend (current rate extrapolated)
  for (let y = CURRENT_YEAR + 1; y <= ZAN_TARGET_YEAR; y++) {
    chartData.push({
      year: y,
      realized: null,
      trajectory: Math.max(
        0,
        data.consumed_ha + data.trajectory_required * (y - CURRENT_YEAR),
      ),
      trend: data.consumed_ha + currentRate * (y - CURRENT_YEAR),
    });
  }

  const gaugeColor = pctConsumed > 80 ? MARIANNE : pctConsumed > 60 ? WARN : SUCCESS;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-4 p-5">

      {/* ── Bandeau ZAN ──────────────────────────────────────────────────────── */}
      <div
        className="flex items-center gap-4 rounded-panel px-5 py-3.5 flex-wrap"
        style={{
          background: isAlerte
            ? 'linear-gradient(100deg,#c10f2e,#ef5a2a)'
            : 'linear-gradient(100deg,#161d3c,#2c3a78)',
        }}
        role="region"
        aria-label="Suivi Zéro Artificialisation Nette"
      >
        <Trees size={22} aria-hidden="true" className="flex-shrink-0 text-white/80" />
        <span className="font-display text-[15px] font-extrabold text-white">
          Suivi ZAN — Zéro Artificialisation Nette {data.detail.zan.objectif_annee}
        </span>

        {isAlerte && (
          <div className="flex items-center gap-1.5 rounded-[9px] bg-white/10 px-3 py-1.5">
            <AlertTriangle size={13} aria-hidden="true" className="text-white" />
            <span className="text-[13px] font-semibold text-white">
              Rythme au-dessus de la cible
            </span>
          </div>
        )}

        <span className="text-[13px] text-white/80">
          Objectif : réduire de {data.detail.zan.reduction_cible_pct}&nbsp;% le rythme
          d&apos;artificialisation d&apos;ici {data.detail.zan.objectif_annee}
        </span>

        <button
          type="button"
          className="ml-auto flex items-center gap-1.5 rounded-[9px] border border-white/30 bg-white/10 px-3 py-1.5 text-[12.5px] font-semibold text-white transition hover:bg-white/20"
          aria-label="Exporter le rapport ZAN"
        >
          <Download size={14} aria-hidden="true" />
          Exporter le rapport
        </button>
      </div>

      {/* ── Rangée KPI (4 cards) ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-4 gap-3">
        <IndicatorCard
          label="Enveloppe ZAN"
          value={data.envelope_ha}
          unit="ha"
          icon={<Layers size={15} />}
          color={NAVY}
        />
        <IndicatorCard
          label="Consommé"
          value={data.consumed_ha}
          unit="ha"
          icon={<BarChart3 size={15} />}
          color={isAlerte ? MARIANNE : WARN}
        />
        <IndicatorCard
          label="Reste disponible"
          value={reste}
          unit="ha"
          icon={<Trees size={15} />}
          color={reste < data.envelope_ha * 0.2 ? MARIANNE : SUCCESS}
          trend={reste < data.envelope_ha * 0.3 ? 'down' : 'stable'}
        />
        <IndicatorCard
          label="Rythme vs cible"
          value={`${rythmeVariation > 0 ? '+' : ''}${rythmeVariation}`}
          unit="%"
          icon={<TrendingDown size={15} />}
          color={rythmeVariation <= 0 ? SUCCESS : MARIANNE}
          trend={rythmeVariation <= 0 ? 'down' : 'up'}
        />
      </div>

      {/* ── Grille 1.5fr / 1fr ───────────────────────────────────────────────── */}
      <div className="grid gap-4" style={{ gridTemplateColumns: '1.5fr 1fr', minHeight: '400px' }}>

        {/* LEFT : Trajectoire cumulée */}
        <div className="rounded-panel border border-line bg-white shadow-card overflow-hidden flex flex-col">
          <div className="border-b border-line px-4 py-3">
            <h3 className="font-display text-[14px] font-bold text-ink">
              Trajectoire cumulée de consommation ZAN
            </h3>
            <p className="text-[11.5px] text-faint">
              Réalisé · tendance · trajectoire requise ({CURRENT_YEAR}–{ZAN_TARGET_YEAR})
              · Plafond {data.envelope_ha} ha
            </p>
          </div>

          <div className="flex-1 p-4" style={{ minHeight: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 8, right: 56, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e8f0" />
                <XAxis
                  dataKey="year"
                  tick={{ fontSize: 11, fill: '#8b93a9' }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#8b93a9' }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v: number) => `${v} ha`}
                  width={56}
                />
                <Tooltip
                  contentStyle={{
                    fontSize: 12,
                    borderRadius: 8,
                    border: '1px solid #e4e8f0',
                    boxShadow: '0 4px 12px rgba(17,25,58,.1)',
                  }}
                  labelFormatter={(label) => `Année ${label}`}
                  formatter={(value) => {
                    if (typeof value === 'number') return `${value.toFixed(2)} ha`;
                    return String(value);
                  }}
                />
                {/* Envelope ceiling */}
                <ReferenceLine
                  y={data.envelope_ha}
                  stroke={MARIANNE}
                  strokeDasharray="6 3"
                  strokeWidth={1.5}
                  label={{
                    value: `Plafond ${data.envelope_ha} ha`,
                    position: 'right',
                    fontSize: 10,
                    fill: MARIANNE,
                  }}
                />
                {/* 2030 intermediate marker */}
                <ReferenceLine
                  x={2030}
                  stroke={MARIANNE}
                  strokeDasharray="4 2"
                  strokeWidth={1}
                  label={{ value: '2030', position: 'top', fontSize: 10, fill: MARIANNE }}
                />
                {/* Realized area (filled navy) */}
                <Area
                  type="monotone"
                  dataKey="realized"
                  name="Réalisé"
                  fill="#dbeafe"
                  stroke={NAVY}
                  strokeWidth={2}
                  dot={false}
                  connectNulls={false}
                />
                {/* Required trajectory (dashed green) */}
                <Line
                  type="monotone"
                  dataKey="trajectory"
                  name="Trajectoire requise"
                  stroke={SUCCESS}
                  strokeWidth={2}
                  strokeDasharray="6 3"
                  dot={false}
                  connectNulls={false}
                />
                {/* Current rate trend (dashed red) */}
                <Line
                  type="monotone"
                  dataKey="trend"
                  name="Tendance actuelle"
                  stroke={MARIANNE}
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                  dot={false}
                  connectNulls={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Légende manuelle */}
          <div className="flex flex-wrap items-center gap-5 border-t border-line px-4 py-2.5">
            <span className="flex items-center gap-1.5 text-[11px] text-faint">
              <span
                className="inline-block h-2.5 w-5 rounded-[2px]"
                style={{ background: '#dbeafe', border: `1.5px solid ${NAVY}` }}
                aria-hidden="true"
              />
              Réalisé
            </span>
            <span className="flex items-center gap-1.5 text-[11px] text-faint">
              <span
                className="inline-block h-px w-5 border-t-2 border-dashed"
                style={{ borderColor: SUCCESS }}
                aria-hidden="true"
              />
              Trajectoire requise
            </span>
            <span className="flex items-center gap-1.5 text-[11px] text-faint">
              <span
                className="inline-block h-px w-5 border-t-2 border-dashed"
                style={{ borderColor: MARIANNE }}
                aria-hidden="true"
              />
              Tendance actuelle
            </span>
            <span className="flex items-center gap-1.5 text-[11px] text-faint">
              <span
                className="inline-block h-px w-5 border-t-2 border-dashed opacity-60"
                style={{ borderColor: MARIANNE }}
                aria-hidden="true"
              />
              Plafond enveloppe
            </span>
          </div>
        </div>

        {/* RIGHT : Jauge + Budget + Décomposition */}
        <div className="flex flex-col gap-3">

          {/* Taux de consommation (jauge SVG) */}
          <div className="rounded-panel border border-line bg-white shadow-card p-4">
            <h4 className="font-display text-[13px] font-bold text-ink mb-3">
              Taux de consommation
            </h4>
            <div className="flex items-center gap-4">
              <Gauge pct={pctConsumed} color={gaugeColor} />
              <div>
                <p className="text-[13px] font-semibold text-ink">
                  {pctConsumed}&nbsp;% de l&apos;enveloppe
                </p>
                <p className="text-[11.5px] text-faint mt-0.5">
                  {data.consumed_ha} ha consommés / {data.envelope_ha} ha totaux
                </p>
                <p className="text-[11px] mt-1.5 font-semibold" style={{ color: gaugeColor }}>
                  {pctConsumed > 80
                    ? '⚠ Enveloppe quasi-épuisée'
                    : pctConsumed > 60
                    ? 'Vigilance requise'
                    : 'Marge disponible'}
                </p>
              </div>
            </div>
          </div>

          {/* Budget foncier */}
          <div className="rounded-panel border border-line bg-white shadow-card p-4">
            <h4 className="font-display text-[13px] font-bold text-ink mb-3">
              Budget foncier ZAN
            </h4>
            <div className="flex flex-col gap-3">
              {/* Consommé */}
              <div>
                <div className="mb-1 flex justify-between text-[12px]">
                  <span className="text-muted">Consommé</span>
                  <span className="font-semibold text-ink">{data.consumed_ha} ha</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-surface-subtle">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${pctConsumed}%`,
                      background: gaugeColor,
                    }}
                  />
                </div>
              </div>
              {/* Disponible */}
              <div>
                <div className="mb-1 flex justify-between text-[12px]">
                  <span className="text-muted">Disponible</span>
                  <span className="font-semibold" style={{ color: SUCCESS }}>{reste} ha</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-surface-subtle">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${100 - pctConsumed}%`,
                      background: SUCCESS,
                    }}
                  />
                </div>
              </div>
              {/* Quota restant */}
              <p className="mt-1 text-[11px] text-faint">
                Quota restant :{' '}
                <span className="font-semibold text-ink">
                  {data.detail.zan.quota_restant_ha} ha
                </span>{' '}
                (objectif {data.detail.zan.objectif_annee})
              </p>
            </div>
          </div>

          {/* Décomposition de l'artificialisation */}
          <div className="flex-1 rounded-panel border border-line bg-white shadow-card p-4">
            <h4 className="font-display text-[13px] font-bold text-ink mb-3">
              Décomposition de l&apos;artificialisation
            </h4>
            <div className="flex flex-col gap-2.5">
              {[
                { label: 'Habitat (bâti)', value: batimentsHa, color: NAVY },
                { label: 'Activité économique', value: activiteHa, color: WARN },
                { label: 'Infrastructure (voirie)', value: voirieHa, color: '#8a93a8' },
              ].map(({ label, value, color }) => (
                <div key={label} className="flex items-center gap-3">
                  {/* Carré de couleur — forme + couleur (accessibilité) */}
                  <span
                    className="h-2.5 w-2.5 flex-shrink-0 rounded-[2px]"
                    style={{ background: color }}
                    aria-hidden="true"
                  />
                  <span className="flex-1 text-[12px] text-muted">{label}</span>
                  <span className="font-mono text-[12px] font-semibold text-ink">
                    {value.toFixed(2)} ha
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-faint">
              Rythme estimé :{' '}
              <span className="font-semibold text-ink">
                {data.detail.zan.rythme_estime_ha_an} ha/an
              </span>
              {' '}· Cible :{' '}
              <span className="font-semibold" style={{ color: SUCCESS }}>
                {data.trajectory_required} ha/an
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Sources de données */}
      <p className="pb-1 text-[10.5px] text-faint">
        Sources : Fichiers fonciers (DGAAF/CEREMA), BDTOPO (bâti), IGN (réseau routier).
        Cadre : Loi Climat &amp; Résilience (Art. 191-199) — objectif ZAN 2031 et 2050.
        Valeurs calculées depuis les données réelles du territoire (DEMO).
      </p>
    </div>
  );
}
